import type { ProposalDocV1 } from '../../worker/render/types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: string[],
  ) {
    super(message);
  }
}

export interface TotpStatus {
  enabled: boolean;
  pending: boolean;
}

function extractMessage(body: unknown): string {
  return typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as Record<string, unknown>).error === 'string'
    ? (body as Record<string, unknown>).error as string
    : 'Something went wrong.';
}

function extractDetails(body: unknown): string[] | undefined {
  const details =
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>).details : undefined;
  return Array.isArray(details) ? details.filter((item): item is string => typeof item === 'string') : undefined;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(extractMessage(body), response.status, extractDetails(body));
  }
  return body as T;
}

export const authApi = {
  setup: (password: string, bootstrapSecret: string) =>
    request<{ ok: true }>('/api/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ password, bootstrapSecret }),
    }),
  login: (password: string, code?: string) =>
    request<{ ok: true }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    }),
  me: () => request<{ ok: true }>('/api/auth/me'),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  totpStatus: () => request<TotpStatus>('/api/auth/totp'),
  setupTotp: () =>
    request<{ secret: string; otpauth_url: string }>('/api/auth/totp/setup', {
      method: 'POST',
    }),
  enableTotp: (code: string) =>
    request<{ ok: true }>('/api/auth/totp/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  disableTotp: (password: string, code: string) =>
    request<{ ok: true }>('/api/auth/totp/disable', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    }),
};

// ---------------------------------------------------------------------------
// Admin API, mirrors platform/src/worker/routes/admin.ts route-for-route.
// ---------------------------------------------------------------------------

export type ProposalStatus = 'draft' | 'published' | 'signed' | 'archived';

export interface AdminProposalSummary {
  id: string;
  slug: string;
  client_name: string;
  title: string;
  status: ProposalStatus;
  doc_version: number;
  created_at: number;
  updated_at: number;
  published_at: number | null;
  signed_at: number | null;
  archived_at: number | null;
  open_count: number | null;
  first_viewed_at: number | null;
  last_viewed_at: number | null;
  signed_by: string | null;
  packages: Array<{ name: string; price: number }>;
  recommended_package: string | null;
}

export interface AdminProposalDetail {
  id: string;
  slug: string;
  client_name: string;
  title: string;
  status: ProposalStatus;
  doc_version: number;
  schema_version: number;
  created_at: number;
  updated_at: number;
  published_at: number | null;
  signed_at: number | null;
  archived_at: number | null;
  doc: ProposalDocV1;
  signed_by: string | null;
}

export interface AdminVersionSummary {
  id: string;
  doc_version: number;
  reason: string;
  created_at: number;
}

export interface AdminVersionDetail extends AdminVersionSummary {
  doc: ProposalDocV1;
}

export interface AdminViewsMeta {
  first_viewed_at: number | null;
  last_viewed_at: number | null;
  open_count: number;
}

export interface AdminViewEvent {
  event_type: string;
  section: string | null;
  dwell_ms: number | null;
  created_at: number;
}

export interface AdminSaveOk {
  ok: true;
  doc_version: number;
  updated_at: number;
}

export interface AdminBackupItem {
  key: string;
  uploaded_at: string;
  size_bytes: number;
}

export interface AdminBackupStatus {
  cron: string;
  last_backup_at: string | null;
  backup_count: number;
  latest_size_bytes: number | null;
  retention_count: number;
  recent: AdminBackupItem[];
}

export interface AdminProductionError {
  id: number;
  occurred_at: number;
  method: string;
  route: string;
  message: string;
  stack: string | null;
}

/** PUT /proposals/:id, the two 409 reasons are load-bearing: 'stale' means
 *  someone else saved first, 'signed' means the proposal got signed out from
 *  under this edit. The editor reacts very differently to each. */
export interface AdminSaveConflict {
  ok: false;
  reason: 'stale' | 'signed';
  status: 409;
  error: string;
}

export type AdminSaveResult = AdminSaveOk | AdminSaveConflict;

/** PUT /proposals/:id/override-save, same idea, but 'not-signed' replaces
 *  'signed' (you can only override-save a proposal that IS signed). */
export interface AdminOverrideSaveOk {
  ok: true;
  doc_version: number;
}

export interface AdminOverrideSaveConflict {
  ok: false;
  reason: 'stale' | 'not-signed';
  status: 409;
  error: string;
}

export type AdminOverrideSaveResult = AdminOverrideSaveOk | AdminOverrideSaveConflict;

async function putWithConflict<TOk, TReason extends string>(
  path: string,
  body: Record<string, unknown>,
): Promise<TOk | { ok: false; reason: TReason; status: 409; error: string }> {
  const response = await fetch(path, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed: unknown = await response.json().catch(() => ({}));
  if (response.status === 409) {
    const record = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
    return {
      ok: false,
      reason: (record.reason as TReason) ?? ('stale' as TReason),
      status: 409,
      error: extractMessage(parsed),
    };
  }
  if (!response.ok) {
    throw new ApiError(extractMessage(parsed), response.status, extractDetails(parsed));
  }
  return parsed as TOk;
}

export const adminApi = {
  getBackups: () => request<AdminBackupStatus>('/api/admin/backups'),

  runBackup: () =>
    request<{
      ok: true; key: string; status: 'succeeded' | 'succeeded-with-warning'; warning?: string;
    } & (({ status_available: true } & AdminBackupStatus) | { status_available: false })>('/api/admin/backups/run', {
      method: 'POST',
    }),

  listErrors: (limit = 50) =>
    request<{ errors: AdminProductionError[] }>(`/api/admin/errors?limit=${limit}`),

  listProposals: () => request<{ proposals: AdminProposalSummary[] }>('/api/admin/proposals'),

  getProposal: (id: string) => request<AdminProposalDetail>(`/api/admin/proposals/${id}`),

  saveProposal: (id: string, body: { doc: ProposalDocV1; doc_version: number }) =>
    putWithConflict<AdminSaveOk, 'stale' | 'signed'>(`/api/admin/proposals/${id}`, body),

  overrideSaveProposal: (id: string, body: { doc: ProposalDocV1; doc_version: number; confirm: string }) =>
    putWithConflict<AdminOverrideSaveOk, 'stale' | 'not-signed'>(
      `/api/admin/proposals/${id}/override-save`,
      body,
    ),

  publish: (id: string) =>
    request<{ ok: true; status?: ProposalStatus; already?: true }>(`/api/admin/proposals/${id}/publish`, {
      method: 'POST',
    }),

  unpublish: (id: string) =>
    request<{ ok: true; status: ProposalStatus }>(`/api/admin/proposals/${id}/unpublish`, {
      method: 'POST',
    }),

  archive: (id: string) =>
    request<{ ok: true; status: ProposalStatus }>(`/api/admin/proposals/${id}/archive`, {
      method: 'POST',
    }),

  rotateSlug: (id: string, slug?: string) =>
    request<{ ok: true; slug: string }>(`/api/admin/proposals/${id}/rotate-slug`, {
      method: 'POST',
      body: JSON.stringify(slug ? { slug } : {}),
    }),

  listVersions: (id: string) => request<{ versions: AdminVersionSummary[] }>(`/api/admin/proposals/${id}/versions`),

  getVersion: (id: string, versionId: string) =>
    request<AdminVersionDetail>(`/api/admin/proposals/${id}/versions/${versionId}`),

  restoreVersion: (id: string, versionId: string) =>
    request<{ ok: true; doc_version: number }>(`/api/admin/proposals/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({ version_id: versionId }),
    }),

  getViews: (id: string) =>
    request<{ meta: AdminViewsMeta | null; events: AdminViewEvent[] }>(`/api/admin/proposals/${id}/views`),

  resetSignature: (id: string, confirm: string) =>
    request<{ ok: true }>(`/api/admin/proposals/${id}/reset-signature`, {
      method: 'POST',
      body: JSON.stringify({ confirm }),
    }),
};
